/**
 * One limit for every from/to date filter on the Project Sites tables and the
 * reports: the two dates may be at most this many days apart.
 *
 * It is the DIFFERENCE between the dates, not the number of days covered — the
 * 1st to the 2nd is a difference of 1. A filter with only one end filled in has
 * no difference to measure and is not limited.
 *
 * The API enforces the same number (server/src/utils/date_range.util.ts); this
 * half exists so the picker stops the user before the request is made.
 */
export const MAX_DATE_FILTER_RANGE_DAYS = 62;

const DAY_MS = 86_400_000;

// A YYYY-MM-DD value is read as written, never through the browser's time zone.
const toDayNumber = (value?: string | null): number | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (match) return Math.floor(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / DAY_MS);
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / DAY_MS) : null;
};

const toDateString = (dayNumber: number): string => new Date(dayNumber * DAY_MS).toISOString().slice(0, 10);

/** Days between two filter values, or null when either is empty or unreadable. */
export const dateRangeDiffDays = (from?: string | null, to?: string | null): number | null => {
  const fromDay = toDayNumber(from);
  const toDay = toDayNumber(to);
  if (fromDay === null || toDay === null) return null;
  return toDay - fromDay;
};

/** Why a from/to pair cannot be applied, or null when it can. */
export const dateRangeError = (
  from: string | null | undefined,
  to: string | null | undefined,
  label: string,
  maxDays: number = MAX_DATE_FILTER_RANGE_DAYS,
): string | null => {
  const diff = dateRangeDiffDays(from, to);
  if (diff === null) return null;
  if (diff < 0) return `${label}: the "from" date must be on or before the "to" date.`;
  if (diff > maxDays) return `${label}: the two dates can be at most ${maxDays} days apart (selected ${diff} days).`;
  return null;
};

export type DateRangeTriple = [from: string | null | undefined, to: string | null | undefined, label: string];

/** The first offending range of several, or null. */
export const firstDateRangeError = (
  ranges: DateRangeTriple[],
  maxDays: number = MAX_DATE_FILTER_RANGE_DAYS,
): string | null => {
  for (const [from, to, label] of ranges) {
    const error = dateRangeError(from, to, label, maxDays);
    if (error) return error;
  }
  return null;
};

/** `max` for a "to" input once "from" is chosen: from + limit. */
export const maxToDateFor = (
  from?: string | null,
  maxDays: number = MAX_DATE_FILTER_RANGE_DAYS,
): string | undefined => {
  const day = toDayNumber(from);
  return day === null ? undefined : toDateString(day + maxDays);
};

/** `min` for a "from" input once "to" is chosen: to − limit. */
export const minFromDateFor = (
  to?: string | null,
  maxDays: number = MAX_DATE_FILTER_RANGE_DAYS,
): string | undefined => {
  const day = toDayNumber(to);
  return day === null ? undefined : toDateString(day - maxDays);
};

/**
 * `min` / `max` attributes for the two inputs of one range, so the native
 * picker greys out everything that would break the limit:
 *
 *   <input type="date" {...bounds.from} />  <input type="date" {...bounds.to} />
 */
export const dateRangeInputBounds = (
  from?: string | null,
  to?: string | null,
  maxDays: number = MAX_DATE_FILTER_RANGE_DAYS,
): { from: { min?: string; max?: string }; to: { min?: string; max?: string } } => ({
  from: { min: minFromDateFor(to, maxDays), max: to || undefined },
  to: { min: from || undefined, max: maxToDateFor(from, maxDays) },
});
